import { useAiuiContext } from '../AiuiProvider/index.js'
import { formatVariantPrice } from './shelfDisplay.js'
import Picture from '../../components/picture.js'
import Badge from '../../components/badge.js'
import { cn } from '../../helpers/utils.js'
import { Text } from '../../components/text.js'
import Button from '../../components/button.js'
import { gaTrack } from '../../shared/track.js'
import { trackUrlRef } from '../../shared/trackUrlRef.js'
import { Heading } from '../../components/heading.js'
import type { ShelfDisplayItem, ShelfDisplayType } from './shelfDisplay.js'
import { useExposure } from '../../hooks/useExposure.js'
import { useRef, useEffect, useMemo, useState } from 'react'

const componentType = 'image'
const componentName = 'product_shelf'

const SOLD_OUT_PRICE = 9999999.99

// 公共函数：获取产品图片URL和altText
export const getProductImage = (data: any) => {
  const sku = data?.sku
  const skuArray = data?.variants
  const findSku = skuArray?.find((item: any) => item?.sku === sku)
  const imageUrl = findSku?.image?.url || skuArray?.[0]?.image?.url || ''
  const altText = findSku?.image?.altText || skuArray?.[0]?.image?.altText || data?.custom_name || data?.title || ''

  return {
    imageUrl,
    altText,
  }
}

export const ShelfDisplayWrapItem = ({ data, configuration }: { data: any; configuration?: any }) => {
  const {
    isDisplayBackImage = false,
    itemShape,
    metafields,
    isTopTag = false,
    isShowTag,
    isShowOriginalPrice,
    isShowShortTitle = false,
  } = configuration || {}
  const { locale = 'es', copyWriting, currencyDisplay } = useAiuiContext()
  const { discounts, discountsCopy } = metafields || {}
  const ref = useRef<HTMLDivElement>(null)
  const [showTags, setShowTags] = useState<string[]>([])
  const [currentPriceTag, setCurrentPriceTag] = useState<string>('')

  const onPrimaryButton = (params: ShelfDisplayItem, index: number, data: ShelfDisplayType, coupon: any) => {
    const { primaryFun, onAddCart, onBuyNow, onLearnMore, event } = configuration || {}

    // 优先使用 function props（根据 primaryFun 值）
    if (primaryFun) {
      if (primaryFun === 'AddCart' && onAddCart) {
        return onAddCart(params, index + 1, data, coupon)
      }
      if (primaryFun === 'BuyNow' && onBuyNow) {
        return onBuyNow(params, index + 1, data, coupon)
      }
      if (primaryFun === 'LearnMore' && onLearnMore) {
        return onLearnMore(params, index + 1, data, coupon)
      }
    }

    // 兼容旧的 event 逻辑
    event?.primaryButton?.(params, index + 1, data, coupon)
  }

  const onSecondaryButton = (params: ShelfDisplayItem, index: number, data: ShelfDisplayType, coupon: any) => {
    const { secondaryFun, onAddCart, onBuyNow, onLearnMore, event } = configuration || {}

    // 优先使用 function props（根据 secondaryFun 值）
    if (secondaryFun) {
      if (secondaryFun === 'AddCart' && onAddCart) {
        return onAddCart(params, index + 1, data, coupon)
      }
      if (secondaryFun === 'BuyNow' && onBuyNow) {
        return onBuyNow(params, index + 1, data, coupon)
      }
      if (secondaryFun === 'LearnMore' && onLearnMore) {
        return onLearnMore(params, index + 1, data, coupon)
      }
    }

    // 兼容旧的 event 逻辑
    event?.secondaryButton?.(params, index + 1, data, coupon)
  }

  const variant = useMemo(() => {
    const variants = data?.variants || []
    if (!variants.length) {
      return undefined
    }
    if (!data?.sku) {
      return variants?.[0]
    }
    return variants?.find?.((item: any) => item?.sku === data?.sku) || variants[0]
  }, [data?.sku, data?.variants])

  const variantArr = variant?.id?.split?.('/') || []
  const variantId = variantArr?.[variantArr?.length - 1]

  const isSoldOut =
    !variant?.availableForSale || variant?.price?.amount === SOLD_OUT_PRICE || variant?.price === SOLD_OUT_PRICE

  // active 的 通用折扣
  const coupon = variant?.coupons?.[0]

  const shouldUseCouponPrice = Boolean(isShowOriginalPrice && coupon)
  const currencyCode = data?.price?.currencyCode || 'USD'

  const priceInfo = useMemo(
    () =>
      formatVariantPrice({
        locale,
        amount: shouldUseCouponPrice ? coupon?.variant_price4wscode : variant?.price,
        baseAmount: shouldUseCouponPrice ? variant?.price : 0,
        currencyCode,
        currencyDisplay,
      }),
    [currencyCode, locale, shouldUseCouponPrice, coupon?.variant_price4wscode, variant]
  )

  const { price, basePrice, discount, discountAmount } = priceInfo

  const { imageUrl, altText } = getProductImage(data)

  const displayTitle = data?.custom_name || data?.title
  const currentDisplayTitle = isShowShortTitle
    ? variant?.metafields?.infos?.page_short_title || displayTitle
    : displayTitle
  const displayDescription = data?.custom_description || data?.description

  const showPrice = () => {
    if (coupon?.value_type === 'fixed_amount') {
      const amountStr = discountAmount || ''
      const match = amountStr.match(/^(.*?)(\d[\d.,]*)(.*)$/)
      if (match) {
        const [, prefix, numeric, suffix] = match
        let updatedNumeric = numeric
        if (numeric.endsWith('.00')) {
          updatedNumeric = numeric.replace(/\.00$/, '')
        } else if (numeric.endsWith(',00')) {
          updatedNumeric = numeric.replace(/,00$/, '')
        }
        return `${prefix}${updatedNumeric}${suffix}`
      }
      return amountStr
    }
    return discount || ''
  }

  // 处理标签
  useEffect(() => {
    let handleTags: string[] = []
    if (discount || discountAmount) {
      const discountTag = `${showPrice()} ${discounts?.off || discountsCopy?.off || ''}`
      setCurrentPriceTag(discountTag)
      handleTags.push(discountTag)
    }
    const newTags = data?.tags
      ?.filter?.((item: string) => item?.startsWith?.('CLtag'))
      ?.map?.((item: string) => item?.replace?.('CLtag:', ''))
      ?.slice?.(0, discount ? 1 : 2)
    setShowTags(handleTags.concat(newTags))
  }, [data?.tags, discount, discountAmount, discounts?.off, discountsCopy?.off])

  useExposure(ref, {
    componentType,
    componentName,
    componentTitle: displayTitle,
    componentDescription: displayDescription,
    position: configuration?.index + 1,
  })

  const bottomContent = () => {
    return (
      <>
        {isShowTag && showTags?.length > 0 ? (
          <div className="mb-1 box-border flex h-8 flex-wrap gap-1 overflow-hidden">
            {showTags?.map?.((item: any, index: number) => (
              <Badge key={index} className="shelf-items-tag">
                {item}
              </Badge>
            ))}
          </div>
        ) : null}
        {currentDisplayTitle ? (
          <Heading
            as="h3"
            title={currentDisplayTitle || ''}
            size={2}
            className="shelf-display-product-title lg-desktop:h-[58px] line-clamp-2 h-12"
            html={currentDisplayTitle || ''}
          />
        ) : null}
        <Text
          size={2}
          className="lg-desktop:text-lg desktop:h-[24px] lg-desktop:h-[28px] desktop:text-base shelf-display-product-description line-clamp-1 h-[20px] text-sm"
          html={displayDescription || ''}
        />
        <div className="mb-2 mt-4 flex items-center">
          {isSoldOut ? (
            <div className="tablet:text-2xl text-info-primary text-xl font-bold">{copyWriting?.soldOutText}</div>
          ) : (
            <>
              <div className="final-price tablet:text-2xl text-info-primary text-xl font-bold">
                {variant?.availableForSale ? price || '' : ''}
              </div>
              <div className="origin-price tablet:text-xl text-info-secondary ml-1 text-lg font-bold line-through">
                {variant?.availableForSale ? basePrice || '' : ''}
              </div>
            </>
          )}
        </div>
        {/* 按钮组 */}
        <div
          className={cn(
            'shelf-flex-button-group',
            'lg-desktop:gap-3 flex items-center gap-2',
            configuration.direction === 'vertical' ? 'flex-col' : ''
          )}
        >
          {configuration?.secondaryButton ? (
            <Button
              disabled={isSoldOut && configuration?.secondaryFun !== 'LearnMore'}
              variant="secondary"
              href={data?.custom_secondary_link}
              as={data?.custom_secondary_link ? 'a' : 'button'}
              onClick={() =>
                !data?.custom_secondary_link && onSecondaryButton(data, configuration?.index, configuration, coupon)
              }
              className={configuration.direction === 'vertical' ? 'w-full' : ''}
            >
              {configuration?.secondaryButton || ''}
            </Button>
          ) : null}
          {configuration?.primaryButton ? (
            <Button
              disabled={isSoldOut && configuration?.primaryFun !== 'LearnMore'}
              variant="primary"
              href={data?.custom_primary_link}
              as={data?.custom_primary_link ? 'a' : 'button'}
              onClick={() =>
                !data?.custom_primary_link && onPrimaryButton(data, configuration?.index, configuration, coupon)
              }
              className={configuration.direction === 'vertical' ? 'w-full' : ''}
            >
              {configuration?.primaryButton || ''}
            </Button>
          ) : null}
        </div>
      </>
    )
  }

  return (
    <div
      ref={ref}
      key={data?.id || data?.handle}
      className={cn(
        'bg-background tablet:hover:bg-info-white box-border w-full cursor-pointer overflow-hidden duration-300',
        itemShape === 'round' ? 'rounded-2xl' : 'rounded-none',
        'lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[480] desktop:aspect-w-[316] desktop:aspect-h-[384]',
        'laptop:aspect-w-[288] laptop:aspect-h-[360] aspect-w-[296] aspect-h-[360] relative',
        'md-tablet:h-[360px] shelf-display-item'
      )}
    >
      {isDisplayBackImage ? (
        <div className="absolute inset-0 box-border overflow-hidden">
          <div className="relative inset-0 size-full">
            <Picture
              source={imageUrl}
              alt={altText}
              className="flex h-full justify-center object-cover [&_img]:w-auto"
            />
            <div className="desktop:p-6 absolute inset-x-0 bottom-0 box-border overflow-hidden p-4">
              {bottomContent()}
            </div>
          </div>
        </div>
      ) : (
        <div className="desktop:p-6 absolute inset-0 box-border flex flex-col justify-between overflow-hidden p-4">
          {currentPriceTag && isTopTag && (
            <Badge className="shelf-prices-tag absolute left-4 top-4 z-10">{currentPriceTag || ''}</Badge>
          )}
          <div
            className={cn(
              'lg-desktop:h-[195px] shelf-display-item-image relative mb-2 inline-block h-[140px] w-full flex-1 overflow-hidden'
            )}
          >
            <a
              aria-label={displayTitle}
              target={configuration?.target}
              href={trackUrlRef(
                `${locale === 'us' || !locale ? '' : `/${locale}`}/products/${data?.handle}?variant=${variantId}`,
                `${componentType}_${componentName}`
              )}
              onClick={() => {
                gaTrack({
                  event: 'ga4Event',
                  event_name: 'select_item',
                  event_parameters: {
                    page_group: 'Home Page',
                    item_list_name: 'Home_Page_Bundle',
                    items: [
                      {
                        item_id: data?.sku || variant?.sku,
                        item_name: data?.name,
                        item_variant: variant?.name,
                        price: variant?.price,
                        index: configuration?.index + 1,
                      },
                    ],
                  },
                })
              }}
            >
              <Picture
                source={imageUrl}
                alt={altText}
                className="flex h-full justify-center object-cover [&_img]:w-auto"
              />
            </a>
          </div>
          {bottomContent()}
        </div>
      )}
    </div>
  )
}

export const ShelfDisplayHorizontalItem = ({ data, configuration }: { data: any; configuration?: any }) => {
  const { itemShape, itemLength, metafields, isShowShortTitle } = configuration || {}
  const { discounts, discountsCopy } = metafields || {}
  const { locale = 'us', copyWriting, currencyDisplay } = useAiuiContext()
  const [showTags, setShowTags] = useState<string[]>([])
  const ref = useRef<HTMLDivElement>(null)

  const onPrimaryButton = (params: ShelfDisplayItem, index: number, data: ShelfDisplayType, coupon: any) => {
    const { primaryFun, onAddCart, onBuyNow, onLearnMore, event } = configuration || {}

    // 优先使用 function props（根据 primaryFun 值）
    if (primaryFun) {
      if (primaryFun === 'AddCart' && onAddCart) {
        return onAddCart(params, index + 1, data, coupon)
      }
      if (primaryFun === 'BuyNow' && onBuyNow) {
        return onBuyNow(params, index + 1, data, coupon)
      }
      if (primaryFun === 'LearnMore' && onLearnMore) {
        return onLearnMore(params, index + 1, data, coupon)
      }
    }

    // 兼容旧的 event 逻辑
    event?.primaryButton?.(params, index + 1, data, coupon)
  }

  const onSecondaryButton = (params: ShelfDisplayItem, index: number, data: ShelfDisplayType, coupon: any) => {
    const { secondaryFun, onAddCart, onBuyNow, onLearnMore, event } = configuration || {}

    // 优先使用 function props（根据 secondaryFun 值）
    if (secondaryFun) {
      if (secondaryFun === 'AddCart' && onAddCart) {
        return onAddCart(params, index + 1, data, coupon)
      }
      if (secondaryFun === 'BuyNow' && onBuyNow) {
        return onBuyNow(params, index + 1, data, coupon)
      }
      if (secondaryFun === 'LearnMore' && onLearnMore) {
        return onLearnMore(params, index + 1, data, coupon)
      }
    }

    // 兼容旧的 event 逻辑
    event?.secondaryButton?.(params, index + 1, data, coupon)
  }

  const variant = useMemo(() => {
    const variants = data?.variants || []
    if (!variants.length) {
      return undefined
    }
    if (!data?.sku) {
      return variants[0]
    }
    return variants.find((item: any) => item?.sku === data?.sku) || variants[0]
  }, [data?.sku, data?.variants])

  const variantArr = variant?.id?.split?.('/') || []
  const variantId = variantArr?.[variantArr?.length - 1]

  const isSoldOut =
    !variant?.availableForSale || variant?.price?.amount === SOLD_OUT_PRICE || variant?.price === SOLD_OUT_PRICE
  const isShowTag = configuration?.isShowTag
  const isShowOriginalPrice = configuration?.isShowOriginalPrice

  // active 的 通用折扣
  const coupon = variant?.coupons?.[0]

  const shouldUseCouponPrice = Boolean(isShowOriginalPrice && coupon)
  const currencyCode = data?.price?.currencyCode || 'USD'

  const priceInfo = useMemo(
    () =>
      formatVariantPrice({
        locale,
        amount: shouldUseCouponPrice ? coupon?.variant_price4wscode : variant?.price,
        baseAmount: shouldUseCouponPrice ? variant?.price : 0,
        currencyCode,
        currencyDisplay,
      }),
    [currencyCode, locale, shouldUseCouponPrice, coupon?.variant_price4wscode, variant]
  )

  const { price, basePrice, discount, discountAmount } = priceInfo

  const { imageUrl, altText } = getProductImage(data)

  const displayTitle = data?.custom_name || data?.title
  const currentDisplayTitle = isShowShortTitle
    ? variant?.metafields?.infos?.page_short_title || displayTitle
    : displayTitle
  const displayDescription = data?.custom_description || data?.description

  useExposure(ref, {
    componentType,
    componentName,
    componentTitle: displayTitle,
    componentDescription: displayDescription,
    position: configuration?.index + 1,
  })

  const showSizeClass = (): {
    boxItem: string
    imgItem: string
    wrap: string
  } => {
    if (itemLength >= 2) {
      return {
        boxItem: 'lg-desktop:max-w-[401px] desktop:max-w-[292px] max-w-full',
        imgItem:
          'm-tablet:m-auto lg-desktop:max-w-[330px] lg-desktop:max-h-[330px] desktop:max-w-[260px] desktop:max-h-[260px] max-w-[138px] max-h-[138px]',
        wrap: 'lg-desktop:aspect-w-[824] lg-desktop:aspect-h-[480] desktop:aspect-w-[648] desktop:aspect-h-[380] laptop:aspect-w-[440] laptop:aspect-h-[356] tablet:aspect-w-[346] tablet:aspect-h-[360] md-tablet:w-full',
      }
    }
    return {
      boxItem: 'lg-desktop:max-w-[401px] desktop:max-w-[292px] laptop:max-w-[289px] max-w-[262px]',
      imgItem:
        'md-tablet:m-auto lg-desktop:max-w-[450px] lg-desktop:max-h-[450px] desktop:max-w-[332px] desktop:max-h-[332px] max-w-[312px] max-h-[312px]',
      wrap: 'lg-desktop:aspect-w-[1664] lg-desktop:aspect-h-[480] desktop:aspect-w-[1312] desktop:aspect-h-[380] laptop:aspect-w-[896] laptop:aspect-h-[356] tablet:aspect-w-[704] tablet:aspect-h-[360] md-tablet:w-full',
    }
  }

  const handleWrapClass = () => {
    if (itemLength >= 2) {
      return 'flex flex-col justify-between desktop:gap-12 desktop:flex-row desktop:justify-center desktop:items-center'
    }
    return 'flex justify-center items-center gap-6 md-tablet:flex-col'
  }

  const showPrice = () => {
    if (coupon?.value_type === 'fixed_amount') {
      const amountStr = discountAmount || ''
      const match = amountStr.match(/^(.*?)(\d[\d.,]*)(.*)$/)
      if (match) {
        const [, prefix, numeric, suffix] = match
        let updatedNumeric = numeric
        if (numeric.endsWith('.00')) {
          updatedNumeric = numeric.replace(/\.00$/, '')
        } else if (numeric.endsWith(',00')) {
          updatedNumeric = numeric.replace(/,00$/, '')
        }
        return `${prefix}${updatedNumeric}${suffix}`
      }
      return amountStr
    }
    return discount || ''
  }

  // 处理标签
  useEffect(() => {
    let handleTags: string[] = []
    if (discount || discountAmount) {
      const discountTag = `${showPrice()} ${discounts?.off || discountsCopy?.off || ''}`
      handleTags.push(discountTag)
    }
    const newTags = data?.tags
      ?.filter?.((item: string) => item?.startsWith?.('CLtag'))
      ?.map?.((item: string) => item?.replace?.('CLtag:', ''))
      ?.slice?.(0, discount ? 1 : 2)
    setShowTags(handleTags.concat(newTags))
  }, [data?.tags, discount, discountAmount, discounts?.off, discountsCopy?.off])

  return (
    <div
      ref={ref}
      key={data?.id || data?.handle}
      className={cn(
        showSizeClass().wrap,
        itemShape === 'round' ? 'rounded-2xl' : 'rounded-none',
        'shelf-display-item',
        'bg-container-secondary-1 tablet:hover:bg-info-white gap-6 duration-300',
        'md-tablet:h-[360px] relative box-border w-full cursor-pointer overflow-hidden'
      )}
    >
      <div className={cn(handleWrapClass(), 'desktop:p-6 absolute inset-0 box-border overflow-hidden p-4')}>
        <div className={cn(showSizeClass().imgItem, 'desktop:mb-0 relative mb-1 overflow-hidden')}>
          <a
            aria-label={displayTitle}
            target={configuration?.target}
            href={trackUrlRef(
              `${locale === 'us' || !locale ? '' : `/${locale}`}/products/${data?.handle}?variant=${variantId}`,
              `${componentType}_${componentName}`
            )}
            onClick={() => {
              gaTrack({
                event: 'ga4Event',
                event_name: 'select_item',
                event_parameters: {
                  page_group: 'Home Page',
                  item_list_name: 'Home_Page_Bundle',
                  items: [
                    {
                      item_id: data?.sku || variant?.sku,
                      item_name: data?.name,
                      item_variant: variant?.name,
                      price: variant?.price,
                      index: configuration?.index + 1,
                    },
                  ],
                },
              })
            }}
          >
            <Picture
              source={imageUrl}
              alt={altText}
              className="flex h-full justify-center object-cover [&_img]:w-auto"
            />
          </a>
        </div>
        <div className={cn('flex flex-col items-start justify-center', showSizeClass().boxItem)}>
          {isShowTag && (
            <div className="mb-1 box-border flex h-8 flex-wrap gap-1 overflow-hidden">
              {showTags?.length > 0 &&
                showTags?.map?.((item: any, index: number) => (
                  <Badge key={index} className="shelf-items-tag">
                    {item}
                  </Badge>
                ))}
            </div>
          )}
          {currentDisplayTitle ? (
            <Heading
              as="h3"
              title={currentDisplayTitle || ''}
              size={2}
              className="shelf-display-product-title lg-desktop:h-[58px] mb-1 line-clamp-2 h-[48px]"
              html={currentDisplayTitle || ''}
            />
          ) : null}
          {displayDescription ? (
            <Text
              size={2}
              className="lg-desktop:text-lg lg-desktop:h-[26px] desktop:text-base desktop:h-6 shelf-display-product-description line-clamp-1 h-5 text-sm"
              html={displayDescription || ''}
            />
          ) : null}
          <div className="mb-2 mt-5 flex items-center">
            {isSoldOut ? (
              <div className="tablet:text-2xl text-info-primary text-xl font-bold">{copyWriting?.soldOutText}</div>
            ) : (
              <>
                <div className="final-price tablet:text-2xl text-info-primary text-xl font-bold">
                  {variant?.availableForSale ? price || '' : ''}
                </div>
                <div className="origin-price tablet:text-xl text-info-secondary ml-1 text-lg font-bold line-through">
                  {variant?.availableForSale ? basePrice || '' : ''}
                </div>
              </>
            )}
          </div>
          {/* 按钮组 */}
          <div
            className={cn(
              'shelf-flex-button-group',
              'lg-desktop:gap-3 flex items-center gap-2',
              configuration.direction === 'vertical' ? 'flex-col' : ''
            )}
          >
            {configuration?.secondaryButton ? (
              <Button
                variant="secondary"
                href={data?.custom_secondary_link}
                as={data?.custom_secondary_link ? 'a' : 'button'}
                onClick={() =>
                  !data?.custom_secondary_link && onSecondaryButton(data, configuration?.index, configuration, coupon)
                }
                className={configuration.direction === 'vertical' ? 'w-full' : ''}
              >
                {configuration?.secondaryButton || ''}
              </Button>
            ) : null}
            {configuration?.primaryButton ? (
              <Button
                disabled={isSoldOut}
                variant="primary"
                href={data?.custom_primary_link}
                as={data?.custom_primary_link ? 'a' : 'button'}
                onClick={() =>
                  !data?.custom_primary_link && onPrimaryButton(data, configuration?.index, configuration, coupon)
                }
                className={configuration.direction === 'vertical' ? 'w-full' : ''}
              >
                {configuration?.primaryButton || ''}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
